import { ALL_WEEK_DAYS, Office } from "@gettactic/api";
import {
  getDefaultsHoursFromOffice,
  calculateEndTimeFromDuration
} from "@gettactic/helpers";
import { calculateHoursDifference } from "@gettactic/helpers/src/date-time/calculateHoursDifference";
import {
  isWithinWorkingHours,
  TimeWindow
} from "@gettactic/helpers/src/schedules/isWithinWorkingHours";
import { useCallback, useMemo } from "react";
import { useOperationalHours } from "../hours/useOperationalHours";

const WEEK_START_ON_MONDAY = Object.freeze([6, 0, 1, 2, 3, 4, 5]);

export function useDefaultOfficeHours(office: Office) {
  const { id: officeId } = office;
  const workingHours = useOperationalHours(
    officeId ? { id: officeId } : undefined
  );

  // Memoize to prevent creating new objects on every render
  const definedOfficeHour = useMemo(
    () => getDefaultsHoursFromOffice(office),
    [office?.id, office?.settings?.reservation_default_time]
  );

  // getDefaultsHoursFromOffice already calculates endHour and endMinutes
  const definedOfficeHourWindow: TimeWindow = useMemo(
    () => ({
      startHour: definedOfficeHour.startHour,
      startMinutes: definedOfficeHour.startMinutes,
      endHour: definedOfficeHour.endHour,
      endMinutes: definedOfficeHour.endMinutes
    }),
    [definedOfficeHour]
  );

  return useCallback(
    (forDay: Date) => {
      if (!workingHours.isSuccess) {
        return undefined;
      }
      if (workingHours.isFetched && workingHours.data?.elements?.length === 0) {
        return definedOfficeHourWindow;
      }

      const operational = workingHours.data?.elements ?? [];
      const dayOfWeekName =
        ALL_WEEK_DAYS[WEEK_START_ON_MONDAY[forDay.getDay()]];
      const currentWorkingHour = operational.find((x) =>
        x.days?.find((xx) => xx.toLowerCase() === dayOfWeekName)
      );
      const workingHoursOpenWindow = currentWorkingHour
        ? calculateHoursDifference(
            currentWorkingHour.start_time,
            currentWorkingHour.end_time
          )
        : 0;
      const isOpen24 = workingHoursOpenWindow === 24;

      if (!currentWorkingHour) {
        return definedOfficeHourWindow;
      }

      const [startHours, startMinutes] =
        currentWorkingHour.start_time.split(":");
      const operationalStartHour = Number.parseInt(startHours);
      const operationalStartMinutes = Number.parseInt(startMinutes);

      // Calculate end time from operational hours start + desk duration
      const { endHour: operationalEndHour, endMinutes: operationalEndMinutes } =
        calculateEndTimeFromDuration(
          operationalStartHour,
          operationalStartMinutes,
          definedOfficeHour.deskDuration
        );

      const currentWHValues = {
        startHour: operationalStartHour,
        startMinutes: operationalStartMinutes,
        endHour: operationalEndHour,
        endMinutes: operationalEndMinutes,
        meetingRoomDuration: definedOfficeHour.meetingRoomDuration,
        deskDuration: definedOfficeHour.deskDuration
      };

      // if a currentWorkingHour exists we are going to check if overlaps the defined
      // office hour. In that case we use as default the current working hour
      if (
        !isOpen24 &&
        !isWithinWorkingHours(definedOfficeHourWindow, currentWHValues)
      ) {
        return currentWHValues;
      }
      if (isOpen24) {
        return definedOfficeHourWindow;
      }
      return currentWHValues;
    },
    [
      workingHours.isSuccess,
      officeId,
      definedOfficeHour,
      definedOfficeHourWindow
    ]
  );
}

/**
 * Sets end time on a date object, handling day overflow when end time wraps to next day.
 * If end time is before start time, adds one day to handle overflow.
 */
export const setEndTimeWithDayOverflow = (
  endDate: Date,
  startDate: Date,
  endHour: number,
  endMinutes: number
) => {
  endDate.setHours(endHour, endMinutes, 0);
  // If end time is before or equal to start time, it means it wrapped to the next day
  // Add one day to handle day overflow
  if (endDate.getTime() <= startDate.getTime()) {
    endDate.setDate(endDate.getDate() + 1);
  }
};
